import { useCallback } from "react";

import { listChatGeneratedArtifacts } from "~/entities/chat";
import type { GeneratedArtifactPicker } from "~/entities/chat";
import { fileNameFromKey } from "~/shared/lib/uploads/file-name";

import { useChatMediaPicker } from "../useChatMediaPicker";
import type { PerKindCounts, PerKindLimits, PickerSourceItem, UseAttachmentPickerResult } from "../types";

export interface UsePickGeneratedAttachmentParams {
    chatId: string | null;
    /** Per-kind лимиты режима. */
    limits: PerKindLimits;
    /** Уже в pendingByChat для каждого kind. */
    currentCounts: PerKindCounts;
    enabled: boolean;
}

export type UsePickGeneratedAttachmentResult = UseAttachmentPickerResult;

const toItem = (a: GeneratedArtifactPicker): PickerSourceItem => ({
    id: a.id,
    kind: a.artifactType,
    s3Key: a.s3Key,
    s3Url: a.s3Url,
    fileName: fileNameFromKey(a.s3Key),
    sizeBytes: null,
    mimeType: a.mimeType,
    createdAt: a.createdAt,
});

export function usePickGeneratedAttachment({
    chatId,
    limits,
    currentCounts,
    enabled,
}: UsePickGeneratedAttachmentParams): UsePickGeneratedAttachmentResult {
    // Без type-фильтра — забираем все артефакты чата (image/video).
    // Audio артефакты редки (TTS), но если есть — тоже попадут.
    // Tabs клиентски разделят, accepted-режим клиентски отфильтрует.
    // Лента дочитывается страницами: раньше пикер брал один запрос с потолком
    // 200, и всё, что старше, до пользователя не доходило вовсе.
    const loadPage = useCallback(async (chatUuid: string, cursor: string | undefined, limit: number) => {
        const res = await listChatGeneratedArtifacts(chatUuid, { limit, before: cursor });
        return { items: res.artifacts, total: res.total };
    }, []);

    return useChatMediaPicker<GeneratedArtifactPicker>({
        chatId,
        limits,
        currentCounts,
        enabled,
        loadPage,
        toItem,
    });
}
